import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useState } from 'react';
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNotifications, useMarkRead, useMarkAllRead } from 'api/notifications';
import { useTokens } from 'theme';
import { formatNumber, formatRelative as relativeTime } from 'i18n/format';
import { notificationCopy } from './notificationCopy';

const typeDot = (t) => ({
  pipeline_success: t.positive,
  pipeline_failed:  t.negative,
  dbt_success:      t.positive,
  dbt_failed:       t.negative,
  member_joined:    t.info,
  system:           t.inkDim,
});

function formatRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return translate('shell.justNow');
  if (m < 60) return relativeTime(-m, 'minute');
  const h = Math.floor(m / 60);
  if (h < 24) return relativeTime(-h, 'hour');
  return relativeTime(-Math.floor(h / 24), 'day');
}

export default function NotificationBell() {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const [anchor, setAnchor] = useState(null);
  const { data: notifications = [], isLoading } = useNotifications();
  const markRead    = useMarkRead();
  const markAllRead = useMarkAllRead();

  const unread = notifications.filter((n) => !n.is_read).length;
  const open   = Boolean(anchor);

  const handleOpen  = (e) => setAnchor(e.currentTarget);
  const handleClose = ()  => setAnchor(null);

  const handleClick = (n) => {
    if (!n.is_read) markRead.mutate(n.id);
    if (n.link) { handleClose(); window.location.href = n.link; }
  };

  return (
    <>
      <Tooltip title={translate('shell.notifications')} placement="top">
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={translate('shell.notifications')}
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            color: t.onChromeMuted,
            '&:hover': { bgcolor: t.chromeAlt, color: t.onChrome },
          }}
        >
          <Badge
            badgeContent={unread || null}
            color="error"
            max={99}
          >
            <NotificationsIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        // It lives at the foot of the sidebar: open beside it, growing upwards.
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { width: 360, maxHeight: 480, ml: 1.5 } }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${t.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: t.ink }}>{translate('shell.notifications')}</Typography>
            {unread > 0 && (
              <Box
                sx={{
                  px: '7px',
                  bgcolor: t.accent,
                  borderRadius: 999,
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  color: t.onAccent,
                  lineHeight: 1.6,
                }}
              >
                {formatNumber(unread)}
              </Box>
            )}
          </Box>
          {unread > 0 && (
            <Tooltip title={translate('shell.markAllRead')}>
              <IconButton
                size="small"
                aria-label={translate('shell.markAllRead')}
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                sx={{ color: t.inkMuted, '&:hover': { bgcolor: t.raised, color: t.ink } }}
              >
                {markAllRead.isPending
                  ? <CircularProgress size={14} sx={{ color: t.link }} />
                  : <DoneAllIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Body */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: t.inkMuted }}>{translate('shell.noNotifications')}</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflow: 'auto', maxHeight: 400 }}>
            {notifications.map((notification, idx) => {
              const n = { ...notification, ...notificationCopy(notification) };
              return (
              <React.Fragment key={n.id}>
                {idx > 0 && <Divider />}
                <ListItem
                  alignItems="flex-start"
                  onClick={() => handleClick(n)}
                  sx={{
                    cursor: n.link ? 'pointer' : 'default',
                    bgcolor: n.is_read ? t.surface : t.highlight,
                    borderLeft: n.is_read ? 'none' : `4px solid ${t.link}`,
                    py: 1.25,
                    px: 2,
                    gap: 1.5,
                    '&:hover': { bgcolor: t.raised },
                  }}
                >
                  {/* Type dot */}
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      mt: '4px',
                      bgcolor: typeDot(t)[n.type] || t.inkDim,
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: n.is_read ? 600 : 700, lineHeight: 1.3 }}>
                        {n.title}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.25 }}>
                        {n.body && (
                          <Typography sx={{ fontSize: '0.73rem', fontWeight: 500, color: t.inkMuted, display: 'block' }}>
                            {n.body}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: t.inkMuted, letterSpacing: '0.04em' }}>
                          {formatRelative(n.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
